import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Search, RotateCcw, MapPin, ChevronUp, ChevronDown, ChevronsUpDown, Eye, ChevronLeft, ChevronRight, Clock, Route, Smartphone } from 'lucide-react';
import { toast } from 'react-toastify';
import { useApi } from '../../../hooks/useApi';
import { Skeleton } from '../../../components/ui/skeleton';
import { useAuth } from '../../../contexts/AuthContext';
import { cn } from '../../../lib/utils';

export default function AllMaps() {
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('trip_start_time');
  const [sortDirection, setSortDirection] = useState('desc');
  const [meta, setMeta] = useState(null);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [isLoadingTrip, setIsLoadingTrip] = useState(false);
  const [customerDeviceIds, setCustomerDeviceIds] = useState([]);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const { get } = useApi();
  const { user } = useAuth();
  const normalizedRole = user?.role?.toLowerCase() || '';
  const isCustomer = normalizedRole === 'customer' || normalizedRole === 'user';
  const isPageLoading = isLoading || (isCustomer && !devicesLoaded);

  useEffect(() => {
    if (!isCustomer) {
      setDevicesLoaded(true);
      return;
    }

    const loadCustomerDevices = async () => {
      try {
        const response = await get('/customer/my-vehicles?per_page=1000&page=1');
        const vehicles = Array.isArray(response?.data) ? response.data : [];
        const deviceIds = [
          ...new Set(
            vehicles
              .map((vehicle) => vehicle?.device_id || vehicle?.deviceId)
              .filter(Boolean)
              .map(String)
          ),
        ];
        setCustomerDeviceIds(deviceIds);
      } catch {
        setCustomerDeviceIds([]);
      } finally {
        setDevicesLoaded(true);
      }
    };

    loadCustomerDevices();
  }, [get, isCustomer]);

  const normalizeTripRecord = (trip) => ({
    ...trip,
    _id: trip?._id || trip?.id || trip?.trip_id,
    deviceId: trip?.deviceId || trip?.device_id || trip?.terminalId,
    startTime: trip?.startTime || trip?.trip_start_time || trip?.start_time,
    endTime: trip?.endTime || trip?.trip_end_time || trip?.end_time,
    durationMinutes: trip?.durationMinutes ?? trip?.duration_minutes,
    distanceKm: trip?.distanceKm ?? trip?.distance_km,
    status: trip?.status || trip?.trip_status,
  });

  const sortTrips = (list) => {
    const direction = sortDirection === 'asc' ? 1 : -1;
    const fieldMap = {
      startTime: 'startTime',
      endTime: 'endTime',
      deviceId: 'deviceId',
      durationMinutes: 'durationMinutes',
      distanceKm: 'distanceKm',
      status: 'status',
      trip_start_time: 'startTime',
    };
    const field = fieldMap[sortField] || 'startTime';

    return [...list].sort((a, b) => {
      const left = a[field];
      const right = b[field];

      if (field === 'startTime' || field === 'endTime') {
        const leftTime = left ? new Date(left).getTime() : 0;
        const rightTime = right ? new Date(right).getTime() : 0;
        return (leftTime - rightTime) * direction;
      }

      if (typeof left === 'number' && typeof right === 'number') {
        return (left - right) * direction;
      }

      return String(left || '').localeCompare(String(right || '')) * direction;
    });
  };

  const paginateTrips = (list) => {
    const total = list.length;
    const lastPage = Math.max(1, Math.ceil(total / itemsPerPage));
    const safePage = Math.min(currentPage, lastPage);
    const start = (safePage - 1) * itemsPerPage;

    return {
      pageTrips: list.slice(start, start + itemsPerPage),
      meta: {
        current_page: safePage,
        last_page: lastPage,
        total,
        per_page: itemsPerPage,
      },
    };
  };

  // Debounced search effect
  useEffect(() => {
    if (isCustomer && !devicesLoaded) return;

    const timeoutId = setTimeout(() => {
      fetchTrips();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, devicesLoaded, isCustomer]);

  // Regular effect for other dependencies
  useEffect(() => {
    if (isCustomer && !devicesLoaded) return;
    fetchTrips();
  }, [currentPage, sortField, sortDirection, customerDeviceIds, isCustomer, devicesLoaded]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleRefresh = () => {
    fetchTrips();
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const fetchTrips = async () => {
    if (isCustomer && !devicesLoaded) return;

    try {
      setIsLoading(true);
      
      // Add search parameter to API call
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      if (isCustomer) {
        if (customerDeviceIds.length === 0) {
          setTrips([]);
          setMeta({
            current_page: 1,
            last_page: 1,
            total: 0,
            per_page: itemsPerPage,
          });
          return;
        }

        const responses = await Promise.all(
          customerDeviceIds.map((deviceId) =>
            get(
              `/trips?device_id=${encodeURIComponent(deviceId)}&page=1&per_page=500&sortBy=${sortField}&sortOrder=${sortDirection}${searchParam}`
            )
          )
        );

        const mergedMap = new Map();
        responses.forEach((response) => {
          const list = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
              ? response
              : [];
          list.forEach((trip) => {
            const normalized = normalizeTripRecord(trip);
            const tripId = normalized._id;
            if (tripId) {
              mergedMap.set(String(tripId), normalized);
            }
          });
        });

        let mergedTrips = Array.from(mergedMap.values());

        if (searchTerm.trim()) {
          const query = searchTerm.trim().toLowerCase();
          mergedTrips = mergedTrips.filter((trip) => {
            const deviceId = String(trip.deviceId || '').toLowerCase();
            const tripId = String(trip._id || '').toLowerCase();
            return deviceId.includes(query) || tripId.includes(query);
          });
        }

        mergedTrips = sortTrips(mergedTrips);
        const { pageTrips, meta: customerMeta } = paginateTrips(mergedTrips);
        setTrips(pageTrips);
        setMeta(customerMeta);
      } else {
        const response = await get(`/trips?page=${currentPage}&per_page=${itemsPerPage}&sortBy=${sortField}&sortOrder=${sortDirection}${searchParam}`);
        
        
        if (response?.success && Array.isArray(response?.data)) {
          setTrips(response.data.map(normalizeTripRecord));
          setMeta(response.meta);
        } else {
          setTrips([]);
          setMeta(null);
        }
      }
      
    } catch (error) {
      toast.error('Failed to fetch trips: ' + (error.message || 'Unknown error'));
      setTrips([]);
      setMeta(null);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (minutes) => {
    if (minutes == null || Number.isNaN(Number(minutes))) return '—';
    const value = Number(minutes);
    if (value < 60) {
      return `${value}m`;
    }
    const hours = Math.floor(value / 60);
    const remainingMinutes = value % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  };

  const formatDistance = (km) => {
    if (km == null || Number.isNaN(Number(km))) return '—';
    const value = Number(km);
    if (value < 1) {
      return `${(value * 1000).toFixed(0)}m`;
    }
    return `${value.toFixed(2)}km`;
  };

  const getStatusDisplay = (status) => {
    const normalized = String(status || '').toLowerCase();
    const config = {
      confirmed: { label: 'Confirmed', className: 'font-medium text-emerald-600 dark:text-emerald-400' },
      pending: { label: 'Pending', className: 'font-medium text-amber-600 dark:text-amber-400' },
      cancelled: { label: 'Cancelled', className: 'font-medium text-red-600 dark:text-red-400' },
    };
    const item = config[normalized] || {
      label: status || '—',
      className: 'font-medium text-muted-foreground',
    };
    return <span className={`text-[12px] ${item.className}`}>{item.label}</span>;
  };

  const handleViewMap = async (tripId, deviceId) => {
    try {
      setIsLoadingTrip(true);
      const deviceIdentifier = deviceId || null;
      const requestOptions = deviceIdentifier
        ? { params: { device_id: deviceIdentifier } }
        : undefined;
      
      // Fetch individual trip data
      const response = await get(`/trips/${tripId}`, requestOptions);
      
      // Handle the actual response structure
      if (response?.trip && response?.points) {
        // Transform the response to match expected format
        const transformedTrip = {
          _id: response.trip.id.toString(),
          deviceId: response.trip.device_id,
          startTime: response.trip.trip_start_time,
          endTime: response.trip.trip_end_time,
          startCoords: {
            lat: response.trip.start_lat,
            lng: response.trip.start_lng
          },
          endCoords: {
            lat: response.trip.end_lat,
            lng: response.trip.end_lng
          },
          distanceKm: response.trip.distance_km,
          durationMinutes: response.trip.duration_minutes,
          status: response.trip.trip_status,
          createdAt: response.trip.created_at,
          points: response.points // Include the detailed points for route visualization
        };
        
        setSelectedTrip(transformedTrip);
        // Navigate to map with trip data
        const mapPath = isCustomer ? "/customer/map/view" : "/admin/map/view";
        window.location.href = `${mapPath}?tripId=${tripId}`;
        toast.success('Trip data loaded successfully!');
      } else {
        toast.error('Failed to fetch trip details - invalid response structure');
      }
    } catch (error) {
      toast.error('Failed to fetch trip details: ' + (error.message || 'Unknown error'));
    } finally {
      setIsLoadingTrip(false);
    }
  };

  const totalPages = meta?.last_page || 1;
  const currentTrips = trips;
  const showEmptyState = !isPageLoading && currentTrips.length === 0;
  const emptyStateMessage = searchTerm
    ? 'Try adjusting your search criteria'
    : isCustomer && devicesLoaded && customerDeviceIds.length === 0
      ? 'No GPS devices linked to your vehicles yet'
      : 'No trip data available';

  const totalTrips = meta?.total || 0;
  const perPage = meta?.per_page || itemsPerPage;
  const rangeStart = totalTrips === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const rangeEnd = Math.min(currentPage * perPage, totalTrips);

  const panelClass =
    'overflow-hidden rounded-xl bg-card text-card-foreground shadow-sm dark:bg-[#18181b]';
  const mobileShellClass = 'block md:hidden space-y-2.5';
  const panelToolbarClass = 'flex items-center gap-2 px-0 py-2.5 sm:px-4';
  const panelFooterClass =
    'flex flex-col items-start justify-between gap-2 px-0 py-2.5 sm:flex-row sm:items-center sm:px-4';
  const searchWrapClass = 'relative min-w-0 flex-1';
  const searchInputClass =
    'h-10 w-full rounded-full border-0 bg-muted pl-10 pr-4 text-[13px] text-foreground shadow-none placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0 dark:bg-[#27272a] dark:text-zinc-100 dark:placeholder:text-zinc-500';
  const toolbarButtonClass =
    'h-10 w-10 shrink-0 rounded-full border-0 bg-muted p-0 hover:bg-muted/80 dark:bg-[#27272a] dark:hover:bg-[#2f2f33]';

  const gridHeaderRowClass = 'border-0 hover:bg-transparent';
  const gridHeadClass =
    'h-9 px-3 py-0 text-[12px] font-medium text-muted-foreground select-none whitespace-nowrap';
  const sortableHeadClass = `${gridHeadClass} cursor-pointer hover:text-foreground transition-colors`;
  const gridCellClass =
    'px-3 py-1.5 text-[12px] leading-tight text-foreground align-middle whitespace-nowrap';
  const gridRowBaseClass =
    'h-9 border-0 transition-colors hover:bg-muted/50 dark:hover:bg-[#27272a]';
  const getGridRowClass = (index) =>
    cn(
      gridRowBaseClass,
      index % 2 === 1
        ? 'bg-muted/40 dark:bg-[#1f1f23]'
        : 'bg-background/50 dark:bg-[#18181b]'
    );
  const compactTableClass =
    'min-w-full text-xs [&_td]:p-0 [&_th]:p-0 [&_tr]:h-9 [&_tr]:border-0';
  const actionButtonClass =
    'h-7 w-7 shrink-0 rounded-md p-0 text-zinc-500 shadow-none hover:bg-zinc-100 hover:text-zinc-800 focus-visible:ring-0 focus-visible:ring-offset-0 dark:text-zinc-400 dark:hover:bg-[#27272a] dark:hover:text-zinc-200';

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-3.5 w-3.5 text-muted-foreground/60" />;
    }
    return sortDirection === 'asc'
      ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
      : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />;
  };

  const SkeletonRow = ({ index = 0 }) => (
    <TableRow className={getGridRowClass(index)}>
      <TableCell className={gridCellClass}>
        <Skeleton className="h-3.5 w-28" />
      </TableCell>
      <TableCell className={gridCellClass}>
        <Skeleton className="h-3.5 w-28" />
      </TableCell>
      <TableCell className={`${gridCellClass} hidden sm:table-cell`}>
        <Skeleton className="h-3.5 w-32" />
      </TableCell>
      <TableCell className={gridCellClass}>
        <Skeleton className="h-3.5 w-14" />
      </TableCell>
      <TableCell className={gridCellClass}>
        <Skeleton className="h-3.5 w-16" />
      </TableCell>
      <TableCell className={gridCellClass}>
        <Skeleton className="h-3.5 w-20" />
      </TableCell>
      <TableCell className={gridCellClass}>
        <Skeleton className="h-7 w-7 rounded-md" />
      </TableCell>
    </TableRow>
  );

  const mobileCardClass =
    'overflow-hidden rounded-xl border border-border/50 bg-card p-3.5 shadow-sm transition-transform active:scale-[0.99] dark:border-zinc-800/80 dark:bg-[#1f1f23]';
  const mobileCardInsetClass = 'rounded-lg bg-muted/70 px-2.5 py-2 dark:bg-[#27272a]';
  const mobileListClass = 'space-y-2.5';

  const renderSearchToolbar = () => (
    <div className={panelToolbarClass}>
      <div className={searchWrapClass}>
        <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400 dark:text-zinc-500" />
        <Input
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          className={searchInputClass}
        />
      </div>
      <Button
        variant="ghost"
        size="icon"
        onClick={handleRefresh}
        className={toolbarButtonClass}
        aria-label="Refresh trips"
      >
        <RotateCcw className="h-4 w-4 text-zinc-500 dark:text-zinc-400" />
      </Button>
    </div>
  );

  const MobileTripCardSkeleton = () => (
    <div className={mobileCardClass}>
      <div className="mb-3 flex items-center justify-between">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      <div className="mb-3 grid grid-cols-2 gap-2">
        <Skeleton className="h-14 rounded-lg" />
        <Skeleton className="h-14 rounded-lg" />
      </div>
      <div className="mb-3 flex gap-2">
        <Skeleton className="h-6 w-14 rounded-full" />
        <Skeleton className="h-6 w-16 rounded-full" />
      </div>
      <Skeleton className="mb-3 h-9 w-full rounded-lg" />
      <Skeleton className="h-9 w-full rounded-lg" />
    </div>
  );

  const MobileTripCard = ({ trip }) => (
    <article className={mobileCardClass}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Trip
          </p>
          <p className="truncate font-mono text-[11px] text-zinc-600 dark:text-zinc-300">
            #{String(trip._id).slice(0, 12)}
            {String(trip._id).length > 12 ? '…' : ''}
          </p>
        </div>
        {getStatusDisplay(trip.status)}
      </div>

      <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div className={mobileCardInsetClass}>
          <div className="mb-1 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            <Clock className="h-3 w-3 shrink-0" />
            Start
          </div>
          <p className="text-[11px] font-medium leading-snug text-zinc-900 dark:text-zinc-100">
            {formatDateTime(trip.startTime)}
          </p>
        </div>
        <div className={mobileCardInsetClass}>
          <div className="mb-1 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            <Clock className="h-3 w-3 shrink-0" />
            End
          </div>
          <p className="text-[11px] font-medium leading-snug text-zinc-900 dark:text-zinc-100">
            {formatDateTime(trip.endTime)}
          </p>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
          <Clock className="h-3 w-3" />
          {formatDuration(trip.durationMinutes)}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2.5 py-1 text-[11px] font-semibold text-violet-700 dark:text-violet-400">
          <Route className="h-3 w-3" />
          {formatDistance(trip.distanceKm)}
        </span>
      </div>

      <div className={`mb-3 flex items-start gap-2 ${mobileCardInsetClass}`}>
        <Smartphone className="mt-0.5 h-3.5 w-3.5 shrink-0 text-zinc-500 dark:text-zinc-400" />
        <div className="min-w-0">
          <p className="text-[10px] font-medium uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
            Device
          </p>
          <p className="break-all font-mono text-[10px] leading-snug text-zinc-700 dark:text-zinc-300">
            {trip.deviceId || '—'}
          </p>
        </div>
      </div>

      <Button
        type="button"
        variant="ghost"
        disabled={isLoadingTrip}
        onClick={() => handleViewMap(trip._id, trip.deviceId || trip.device_id || trip.terminalId)}
        className="h-10 w-full rounded-lg bg-muted text-[13px] font-medium text-foreground hover:bg-muted/80 dark:bg-[#27272a] dark:text-zinc-100 dark:hover:bg-[#2f2f33]"
      >
        <Eye className="mr-2 h-4 w-4" />
        {isLoadingTrip ? 'Loading map…' : 'View on map'}
      </Button>
    </article>
  );

  const renderPaginationFooter = () => (
    <div className={panelFooterClass}>
      {isPageLoading ? (
        <>
          <Skeleton className="h-4 w-40" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-20 rounded-lg" />
            <Skeleton className="h-8 w-24 rounded-lg" />
            <Skeleton className="h-8 w-20 rounded-lg" />
          </div>
        </>
      ) : (
        <>
          <p className="text-[12px] text-muted-foreground">
            {totalTrips === 0
              ? 'Showing 0 trips'
              : `Showing ${rangeStart}–${rangeEnd} of ${totalTrips} trips`}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="h-7 gap-1 rounded-md border-0 bg-muted/40 px-2.5 text-[12px] text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Prev
            </Button>
            <span className="inline-flex h-7 min-w-[80px] items-center justify-center rounded-md border-0 bg-muted/40 px-2.5 text-[12px] text-muted-foreground">
              Page {currentPage} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="h-7 gap-1 rounded-md border-0 bg-muted/40 px-2.5 text-[12px] text-muted-foreground hover:bg-muted/60 hover:text-foreground"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <div className="relative w-full max-w-full overflow-hidden">
        <div className="mb-3 ">
          <h1 className="text-lg font-bold text-zinc-900 sm:text-xl dark:text-zinc-100">Trip History</h1>
          {sortField ? (
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              Sorted by {sortField} ({sortDirection === 'asc' ? 'ascending' : 'descending'})
            </p>
          ) : null}
        </div>

        {/* Mobile cards — no white panel wrapper; cards sit on page background */}
        <div className={mobileShellClass}>
          {renderSearchToolbar()}
          <div className={mobileListClass}>
            {isPageLoading ? (
              Array.from({ length: 4 }).map((_, index) => (
                <MobileTripCardSkeleton key={`skeleton-card-${index}`} />
              ))
            ) : showEmptyState ? (
              <div className={`${mobileCardClass} py-12 text-center`}>
                <MapPin className="mx-auto mb-4 h-10 w-10 text-zinc-400 dark:text-zinc-500" />
                <h3 className="mb-2 text-[15px] font-medium text-zinc-900 dark:text-zinc-100">
                  No trips found
                </h3>
                <p className="text-[12px] text-zinc-500 dark:text-zinc-400">{emptyStateMessage}</p>
              </div>
            ) : (
              currentTrips.map((trip) => (
                <MobileTripCard key={trip._id} trip={trip} />
              ))
            )}
          </div>
          {renderPaginationFooter()}
        </div>

        {/* Desktop table panel */}
        <div className={`hidden md:block ${panelClass}`}>
          <div className="px-3 py-2.5 sm:px-4">
            {renderSearchToolbar()}
          </div>
          <div className="overflow-x-auto">
            <Table className={compactTableClass}>
              <TableHeader className="[&_tr]:border-0">
                <TableRow className={gridHeaderRowClass}>
              <TableHead
                className={`${sortableHeadClass} min-w-[120px]`}
                onClick={() => handleSort('startTime')}
              >
                <div className="flex items-center gap-1.5">
                  Start Time
                  {getSortIcon('startTime')}
                </div>
              </TableHead>
              <TableHead
                className={`${sortableHeadClass} min-w-[120px]`}
                onClick={() => handleSort('endTime')}
              >
                <div className="flex items-center gap-1.5">
                  End Time
                  {getSortIcon('endTime')}
                </div>
              </TableHead>
              <TableHead
                className={`${sortableHeadClass} hidden min-w-[140px] sm:table-cell`}
                onClick={() => handleSort('deviceId')}
              >
                <div className="flex items-center gap-1.5">
                  Terminal ID
                  {getSortIcon('deviceId')}
                </div>
              </TableHead>
              <TableHead
                className={`${sortableHeadClass} min-w-[100px]`}
                onClick={() => handleSort('durationMinutes')}
              >
                <div className="flex items-center gap-1.5">
                  Duration
                  {getSortIcon('durationMinutes')}
                </div>
              </TableHead>
              <TableHead
                className={`${sortableHeadClass} min-w-[100px]`}
                onClick={() => handleSort('distanceKm')}
              >
                <div className="flex items-center gap-1.5">
                  Distance
                  {getSortIcon('distanceKm')}
                </div>
              </TableHead>
              <TableHead
                className={`${sortableHeadClass} min-w-[90px]`}
                onClick={() => handleSort('status')}
              >
                <div className="flex items-center gap-1.5">
                  Status
                  {getSortIcon('status')}
                </div>
              </TableHead>
              <TableHead className={`${gridHeadClass} min-w-[80px]`}>
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isPageLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <SkeletonRow key={`skeleton-${index}`} index={index} />
              ))
            ) : showEmptyState ? (
              <TableRow>
                <TableCell colSpan={7} className="py-12 text-center">
                  <MapPin className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
                  <h3 className="mb-2 text-[15px] font-medium">No trips found</h3>
                  <p className="text-[13px] text-muted-foreground">{emptyStateMessage}</p>
                </TableCell>
              </TableRow>
            ) : (
              currentTrips.map((trip, index) => (
                <TableRow key={trip._id} className={getGridRowClass(index)}>
                  <TableCell className={gridCellClass}>
                    {formatDateTime(trip.startTime)}
                  </TableCell>
                  <TableCell className={gridCellClass}>
                    {formatDateTime(trip.endTime)}
                  </TableCell>
                  <TableCell className={`${gridCellClass} hidden font-mono text-muted-foreground sm:table-cell`}>
                    {trip.deviceId || '—'}
                  </TableCell>
                  <TableCell className={`${gridCellClass} font-medium text-emerald-600 dark:text-emerald-400`}>
                    {formatDuration(trip.durationMinutes)}
                  </TableCell>
                  <TableCell className={`${gridCellClass} font-medium text-violet-600 dark:text-violet-400`}>
                    {formatDistance(trip.distanceKm)}
                  </TableCell>
                  <TableCell className={gridCellClass}>
                    {getStatusDisplay(trip.status)}
                  </TableCell>
                  <TableCell className={gridCellClass}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleViewMap(trip._id, trip.deviceId || trip.device_id || trip.terminalId)}
                      disabled={isLoadingTrip}
                      className={actionButtonClass}
                      title="View map"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
          </div>
          <div className="px-3 py-2.5 sm:px-4">
            {renderPaginationFooter()}
          </div>
        </div>
      </div>
    </>
  );
}